import { createContext, useContext, useEffect, useState } from 'react';

/**
 * Data Mode Types
 */
export type DataMode = 'demo' | 'production';

export interface DataModeContextType {
  dataMode: DataMode;
  setDataMode: (mode: DataMode) => void;
  isDemoMode: boolean;
  isProductionMode: boolean;
  toggleDataMode: () => void;
}

/**
 * Data Mode Context
 * Provides application-wide data mode switching capabilities
 */
export const DataModeContext = createContext<DataModeContextType | null>(null);

/**
 * Hook to access data mode context
 */
export const useDataMode = (): DataModeContextType => {
  const context = useContext(DataModeContext);
  if (!context) {
    throw new Error('useDataMode must be used within a DataModeProvider');
  }
  return context;
};

/**
 * Custom hook for data mode state management
 * Handles localStorage persistence and environment variable defaults
 */
export const useDataModeState = (): DataModeContextType => {
  // Live data is the safe operational default. Demo mode remains an explicit opt-in.
  const getDefaultDataMode = (): DataMode => {
    if (import.meta.env.PROD) return 'production';
    try {
      const envMode = import.meta.env.VITE_DEFAULT_DATA_MODE as DataMode;
      const validModes: DataMode[] = ['demo', 'production'];
      return validModes.includes(envMode) ? envMode : 'production';
    } catch {
      return 'production';
    }
  };

  // Initialize state from localStorage or environment default
  const [dataMode, setDataModeState] = useState<DataMode>(() => {
    if (import.meta.env.PROD) return 'production';
    try {
      const stored = localStorage.getItem('supportdesk-data-mode');
      if (stored && ['demo', 'production'].includes(stored)) {
        return stored as DataMode;
      }
    } catch (error) {
      console.warn('Failed to read data mode from localStorage:', error);
    }
    return getDefaultDataMode();
  });

  // Persist data mode changes to localStorage
  const setDataMode = (mode: DataMode) => {
    if (import.meta.env.PROD) mode = 'production';
    try {
      localStorage.setItem('supportdesk-data-mode', mode);
      setDataModeState(mode);
    } catch (error) {
      console.warn('Failed to save data mode to localStorage:', error);
      setDataModeState(mode);
    }
  };

  // Toggle between demo and production modes
  const toggleDataMode = () => {
    const newMode = dataMode === 'demo' ? 'production' : 'demo';
    setDataMode(newMode);
  };

  // Derived boolean states for easier usage
  const isDemoMode = dataMode === 'demo';
  const isProductionMode = dataMode === 'production';


  return {
    dataMode,
    setDataMode,
    isDemoMode,
    isProductionMode,
    toggleDataMode,
  };
};

/**
 * Hook for conditional data loading based on data mode
 * Provides a consistent pattern for components to switch between mock and real data
 */
export const useConditionalData = <TData>(
  productionDataFn: () => Promise<TData> | TData,
  demoData: TData,
  dependencies: any[] = []
): {
  data: TData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
} => {
  const { isDemoMode } = useDataMode();
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState<boolean>(!isDemoMode);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    if (isDemoMode) {
      setData(demoData);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await Promise.resolve(productionDataFn());
      setData(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error occurred');
      setError(error);
      console.error('Production data fetch failed:', error);
      
      // Fallback to demo data on error
      setData(demoData);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when mode changes or dependencies update
  useEffect(() => {
    fetchData();
  }, [isDemoMode, ...dependencies]);

  const refetch = () => {
    fetchData();
  };

  return { data, loading, error, refetch };
};

/**
 * Type-safe hook for API data with mock fallback
 */
export const useApiData = <TApiResponse, TMockData = TApiResponse>(
  apiCall: () => Promise<{ success: boolean; data: TApiResponse; error?: string }>,
  // Accepts a promise because some pages build their sample from a live lookup; it is
  // awaited before it reaches state, so callers still see the resolved type.
  mockData: TMockData | Promise<TMockData>,
  options: {
    enabled?: boolean;
    fallbackToMockOnError?: boolean;
  } = {}
): {
  data: TApiResponse | TMockData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  isUsingMockData: boolean;
} => {
  const { isDemoMode } = useDataMode();
  const { enabled = true, fallbackToMockOnError = true } = options;
  
  const [data, setData] = useState<TApiResponse | TMockData | null>(null);
  const [loading, setLoading] = useState<boolean>(!isDemoMode && enabled);
  const [error, setError] = useState<Error | null>(null);
  const [isUsingMockData, setIsUsingMockData] = useState<boolean>(isDemoMode);

  const fetchData = async () => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }

    if (isDemoMode) {
      // mockData may be a promise (some pages build their sample from a live user list).
      // Awaiting keeps a pending promise out of state, which would render as blank.
      setData(await Promise.resolve(mockData));
      setLoading(false);
      setError(null);
      setIsUsingMockData(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setIsUsingMockData(false);
      
      const response = await apiCall();
      
      if (response.success) {
        setData(response.data);
      } else {
        throw new Error(response.error || 'API request failed');
      }
    } catch (err) {
      const apiError = err instanceof Error ? err : new Error('Unknown API error');
      setError(apiError);
      console.error('API call failed:', apiError);
      
      // Fallback to mock data if enabled
      if (fallbackToMockOnError) {
        setData(await Promise.resolve(mockData));
        setIsUsingMockData(true);
        console.warn('Falling back to mock data due to API error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [isDemoMode, enabled]);

  const refetch = () => {
    fetchData();
  };

  return { data, loading, error, refetch, isUsingMockData };
};

/**
 * Environment configuration helpers
 */
export const getDataModeConfig = () => {
  try {
    return {
      defaultMode: import.meta.env.VITE_DEFAULT_DATA_MODE || 'production',
      allowModeToggle: import.meta.env.VITE_ALLOW_DATA_MODE_TOGGLE !== 'false',
      showDataModeIndicator: import.meta.env.VITE_SHOW_DATA_MODE_INDICATOR !== 'false',
    };
  } catch {
    return {
      defaultMode: 'production',
      allowModeToggle: true,
      showDataModeIndicator: true,
    };
  }
};

/**
 * Mock data utilities
 */
export const createMockApiResponse = <T>(data: T, delay: number = 0): Promise<{
  success: boolean;
  data: T;
  error?: string;
}> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        success: true,
        data,
      });
    }, delay);
  });
};
